// src/components/core/NotificationOptIn.jsx
// Deliberate opt-in control for Web Push (Notifications Commit 2).
// Self-contained: handles support detection, the iOS install-first case, the
// permission request, the subscribe+store flow, and status feedback. Mounted on
// the profile page (MyProfile) near the account actions. NEVER auto-prompts —
// the user must tap "Enable notifications" (TRUE: opt-in only).

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import {
  isPushSupported,
  isIosNeedsInstall,
  getPushPermission,
  enablePush,
  checkPushSubscriptionStatus,
} from '../../lib/pushNotifications';

// Strings live in the `notifications` namespace (translation-batches/2026-07-01-
// notifications-push.sql). English fallbacks render before the batch is applied,
// matching the codebase's t(key) || 'fallback' convention.
function NotificationOptIn({ userId }) {
  const { t } = useTranslation('notifications');
  const [permission, setPermission] = useState('default'); // granted | denied | default | unsupported
  // "On" is an intersection (browser subscription + confirmed push_subscriptions
  // row), not permission alone — permission is granted before the subscribe/store
  // steps that can fail, so it false-positives "on" while those steps are broken.
  const [subscribed, setSubscribed] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setPermission(getPushPermission());
    let cancelled = false;
    checkPushSubscriptionStatus(userId).then(({ on }) => {
      if (!cancelled) {
        setSubscribed(on);
        setCheckingStatus(false);
      }
    });
    return () => { cancelled = true; };
  }, [userId]);

  // Nothing to offer to a guest, or where the browser has no push at all.
  if (!userId || !isPushSupported()) return null;

  // iOS Safari (not installed): push is impossible until "Add to Home Screen".
  // Show the instruction rather than a button that would silently fail.
  if (isIosNeedsInstall()) {
    return (
      <div style={boxStyle}>
        <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}>
          {t('push_ios_hint') || 'To get notifications on iPhone or iPad, first add OurNet to your home screen, then open it from there and enable notifications.'}
        </p>
      </div>
    );
  }

  const handleEnable = async () => {
    setBusy(true);
    setMessage('');
    const { ok, reason } = await enablePush(userId);
    setBusy(false);
    setPermission(getPushPermission());
    if (ok) {
      // Only a genuine success may flip the button "on" — a failure below
      // leaves `subscribed` exactly as it was (never a false "on").
      setSubscribed(true);
      setMessage(t('push_enabled') || 'Notifications are on for this device.');
    } else if (reason === 'denied') {
      setMessage(t('push_denied') || 'Notifications are blocked. Enable them for this site in your browser settings.');
    } else if (reason === 'no-key') {
      setMessage(t('push_unavailable') || 'Notifications aren’t available right now. Please try again later.');
    } else {
      setMessage(t('push_error') || 'Something went wrong turning on notifications. Please try again.');
    }
  };

  // Confirmed on (browser subscription + stored DB row) — offer a re-register
  // (a new device/browser reuses or creates its own subscription via the same button).
  const alreadyOn = subscribed;

  return (
    <div style={boxStyle}>
      <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
        {t('push_title') || 'Notifications'}
      </p>
      <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#6b7280' }}>
        {t('push_description') || 'Get notified on this device when someone comments on your article or posts in a group you own.'}
      </p>
      <button
        onClick={handleEnable}
        disabled={busy || checkingStatus || permission === 'denied'}
        className="w-full py-2 rounded-lg font-semibold text-sm transition-colors"
        style={{
          background: alreadyOn ? '#ffffff' : '#024B99',
          color: alreadyOn ? '#024B99' : '#ffffff',
          border: alreadyOn ? '2px solid #024B99' : 'none',
          cursor: (busy || checkingStatus || permission === 'denied') ? 'not-allowed' : 'pointer',
          opacity: (busy || checkingStatus || permission === 'denied') ? 0.6 : 1,
        }}
      >
        {busy
          ? (t('push_enabling') || 'Enabling…')
          : alreadyOn
            ? (t('push_reenable') || 'Notifications on — enable on this device')
            : (t('push_enable') || 'Enable notifications')}
      </button>
      {(message || permission === 'denied') && (
        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: permission === 'denied' ? '#b91c1c' : '#374151' }}>
          {message || (t('push_denied') || 'Notifications are blocked. Enable them for this site in your browser settings.')}
        </p>
      )}
    </div>
  );
}

const boxStyle = {
  marginTop: '16px',
  padding: '12px',
  background: '#f8f9fa',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
};

export default NotificationOptIn;
